import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import './AutoAuthorizeRulesManager.css';

const TIMEZONES = [
  'America/Tijuana',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Hermosillo',
  'America/Chihuahua',
  'America/Mazatlan',
  'America/Cancun',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
];

const WEEKDAYS = [
  { key: 'monday', short: 'Lun', label: 'Lunes' },
  { key: 'tuesday', short: 'Mar', label: 'Martes' },
  { key: 'wednesday', short: 'Mié', label: 'Miércoles' },
  { key: 'thursday', short: 'Jue', label: 'Jueves' },
  { key: 'friday', short: 'Vie', label: 'Viernes' },
  { key: 'saturday', short: 'Sáb', label: 'Sábado' },
  { key: 'sunday', short: 'Dom', label: 'Domingo' },
];

const SECTIONS = [
  { key: 'textApproved', label: 'Text', help: 'Texto abierto del reporte' },
  { key: 'photoApproved', label: 'Photo', help: 'Foto del reporte' },
  { key: 'lineStartApproved', label: 'Line start', help: 'Ubicación de inicio de fila' },
  { key: 'waitBucketApproved', label: 'Wait to cross', help: 'Espera estimada para cruzar' },
  { key: 'waitingFromPositionApproved', label: 'From position', help: 'Espera desde mi posición' },
  { key: 'locationApproved', label: 'Location', help: 'Ubicación actual' },
];

function emptyFlags(value = false) {
  return SECTIONS.reduce((acc, section) => {
    acc[section.key] = value;
    return acc;
  }, {});
}

function emptyDays(value = false) {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day.key] = emptyFlags(value);
    return acc;
  }, {});
}

export default function AutoAuthorizeRulesManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [timezone, setTimezone] = useState('America/Tijuana');
  const [days, setDays] = useState(emptyDays(false));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/auto_authorize_rules');
        setEnabled(data.enabled !== false);
        setTimezone(data.timezone || 'America/Tijuana');
        setDays({ ...emptyDays(false), ...(data.days || {}) });
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las reglas',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleCell = (dayKey, sectionKey) => {
    setDays((prev) => ({
      ...prev,
      [dayKey]: {
        ...emptyFlags(false),
        ...(prev[dayKey] || {}),
        [sectionKey]: !prev[dayKey]?.[sectionKey],
      },
    }));
  };

  const setDayAll = (dayKey, value) => {
    setDays((prev) => ({
      ...prev,
      [dayKey]: emptyFlags(value),
    }));
  };

  const setSectionAll = (sectionKey, value) => {
    setDays((prev) => {
      const next = { ...prev };
      WEEKDAYS.forEach((day) => {
        next[day.key] = {
          ...emptyFlags(false),
          ...(prev[day.key] || {}),
          [sectionKey]: value,
        };
      });
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.put('/auto_authorize_rules', {
        enabled,
        timezone,
        days,
      });
      setEnabled(data.enabled !== false);
      setTimezone(data.timezone || timezone);
      setDays({ ...emptyDays(false), ...(data.days || {}) });
      toast.current?.show({
        severity: 'success',
        summary: 'Guardado',
        detail: 'Reglas de auto autorizador actualizadas',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron guardar las reglas',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-content-center mt-5">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="auto-auth-page">
      <Toast ref={toast} />

      <header className="auto-auth-header">
        <div>
          <p className="auto-auth-kicker">Community & Mobile</p>
          <h2 className="auto-auth-title">Reglas de auto autorizador</h2>
          <p className="auto-auth-lead">
            Por día y por tipo de sección. Lo marcado se publica solo; lo desmarcado
            sigue yendo a moderación.
          </p>
        </div>
        <Button label="Guardar" icon="pi pi-check" loading={saving} onClick={save} />
      </header>

      <section className="auto-auth-toolbar">
        <label className="auto-auth-switch">
          <InputSwitch checked={enabled} onChange={(e) => setEnabled(e.value)} />
          <span>
            <strong>Auto autorizador activo</strong>
            <small>Si lo apagas, nada se publica sin revisión.</small>
          </span>
        </label>
        <div className="auto-auth-tz">
          <label htmlFor="auto-auth-tz">Zona horaria</label>
          <Dropdown
            inputId="auto-auth-tz"
            value={timezone}
            options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
            onChange={(e) => setTimezone(e.value)}
            style={{ minWidth: 240 }}
          />
        </div>
      </section>

      <div className="auto-auth-table-wrap">
        <table className="auto-auth-table">
          <thead>
            <tr>
              <th className="auto-auth-section-col">Sección</th>
              {WEEKDAYS.map((day) => {
                const allOn = SECTIONS.every((section) => days[day.key]?.[section.key]);
                return (
                  <th key={day.key}>
                    <button
                      type="button"
                      className="auto-auth-day-btn"
                      onClick={() => setDayAll(day.key, !allOn)}
                      title={`${allOn ? 'Quitar' : 'Marcar'} todo el ${day.label}`}
                    >
                      <span>{day.short}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((section) => {
              const allOn = WEEKDAYS.every((day) => days[day.key]?.[section.key]);
              return (
                <tr key={section.key}>
                  <th>
                    <button
                      type="button"
                      className="auto-auth-section-btn"
                      onClick={() => setSectionAll(section.key, !allOn)}
                      title={`${allOn ? 'Quitar' : 'Marcar'} ${section.label} toda la semana`}
                    >
                      <strong>{section.label}</strong>
                      <small>{section.help}</small>
                    </button>
                  </th>
                  {WEEKDAYS.map((day) => {
                    const on = !!days[day.key]?.[section.key];
                    return (
                      <td key={`${day.key}-${section.key}`}>
                        <button
                          type="button"
                          className={`auto-auth-cell ${on ? 'is-on' : 'is-off'}`}
                          onClick={() => toggleCell(day.key, section.key)}
                          aria-pressed={on}
                          aria-label={`${section.label} ${day.label}: ${on ? 'auto' : 'manual'}`}
                        >
                          {on ? 'Auto' : 'Manual'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="auto-auth-footnote">
        Si un reporte trae secciones auto y otras manual, se publica lo auto y lo
        demás queda en <code>partially_approved</code> para que lo revises después.
      </p>
    </div>
  );
}
